import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, List, MessageCircle, Move, MousePointer } from 'lucide-react';

const ONBOARDING_KEY = 'lotespro_onboarding_seen';

const OnboardingOverlay = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem(ONBOARDING_KEY);
    if (!hasSeenOnboarding) {
      // Small delay to let the map load first
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const steps = [
    {
      icon: <List className="w-8 h-8 text-orange-500" />,
      title: 'Explora los lotes',
      description: 'Usa la lista de la izquierda para ver todos los lotes disponibles. Haz clic en cualquier lote para ver sus detalles.',
    },
    {
      icon: <MousePointer className="w-8 h-8 text-blue-500" />,
      title: 'Navega en el mapa',
      description: 'Arrastra el mapa para moverte. Usa los botones + y - para acercar o alejar. Haz clic en un lote para seleccionarlo.',
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-green-500" />,
      title: 'Contacta al vendedor',
      description: 'Cuando encuentres un lote que te interese, usa el boton de WhatsApp para contactar directamente al vendedor.',
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Bienvenido a LotesPro</h2>
              <button onClick={handleSkip} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-orange-100 mt-1">
              Paso {currentStep + 1} de {steps.length}
            </p>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {steps[currentStep].icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {steps[currentStep].title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {steps[currentStep].description}
              </p>
            </motion.div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 pb-4">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-orange-500 w-6'
                    : index < currentStep
                    ? 'bg-orange-300'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 flex justify-between items-center">
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Omitir
            </button>
            <button
              onClick={handleNext}
              className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-semibold text-sm hover:bg-orange-600 transition-colors shadow-md hover:shadow-lg"
            >
              {currentStep < steps.length - 1 ? 'Siguiente' : 'Comenzar'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingOverlay;
